import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllTenantAPI,
  getCurrentTenantAPI,
  getTenantPublishStatusAPI,
  updateTenantAPI,
  updateTenantStatusAPI,
  triggerTenantBuildAPI,
} from "../../api/common/TenantApi";

const initialState = {
  userData: null,
  allUsers: null,
  publishStatus: null,
  publishModalOpen: false,
  publishBuildState: "idle",
  publishError: null,
  loading: false,
};

export const updateTenant = createAsyncThunk(
  "update/tenant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateTenantAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const getCurrentTenant = createAsyncThunk(
  "get/current/tenant",
  async (tenantId, { rejectWithValue }) => {
    try {
      const response = await getCurrentTenantAPI(tenantId);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const getAllTenant = createAsyncThunk(
  "tenant/getAllTenant",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getAllTenantAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);



export const triggerTenantBuild = createAsyncThunk(
  "trigger/tenant-build",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await triggerTenantBuildAPI();
      dispatch(getTenantPublishStatus());
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message || err?.data?.message,
      });
    }
  }
);

export const getTenantPublishStatus = createAsyncThunk(
  "tenant/getTenantPublishStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTenantPublishStatusAPI();
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const updateTenantStatus = createAsyncThunk(
  "update/tenant-status",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateTenantStatusAPI(payload);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    closeTenantPublishModal: (state) => {
      if (state.publishBuildState !== "pending") {
        state.publishModalOpen = false;
      }
    },
  },
  extraReducers: (builder) => {
    //get user
    builder.addCase(getCurrentTenant.pending, (state) => {
      state.loading = false;
    });
    builder.addCase(getCurrentTenant.fulfilled, (state, action) => {
      console.log(action.payload.response);
      state.userData = action.payload.response;
      state.loading = true;
    });
    builder.addCase(getCurrentTenant.rejected, (state) => {
      state.loading = false;
    });

    //update user
    builder.addCase(updateTenant.pending, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTenant.fulfilled, (state, action) => {
      state.userData = action.payload.response;
      state.loading = true;
    });
    builder.addCase(updateTenant.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllTenant.pending, (state) => {
      state.loading = false;
    });
    builder.addCase(getAllTenant.fulfilled, (state, action) => {
      state.allUsers = action.payload?.response;
      state.loading = true;
    });
    builder.addCase(getAllTenant.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateTenantStatus.pending, (state) => {
      state.loading = false;
    });
    builder.addCase(updateTenantStatus.fulfilled, (state, action) => {
      // const { arg } = action.meta;
      // if (arg) {
      //   console.log(arg, state.allUsers, state.userData);
      //   state.allUsers = state.allUsers?.map((item) => {
      //     if (item.tenantId === arg.tenantId) {
      //       item.isActive = arg.statusId;
      //     }
      //     return item;
      //   });
      // }

      state.loading = true;
    });
    builder.addCase(updateTenantStatus.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(triggerTenantBuild.pending, (state) => {
      state.loading = false;
      state.publishModalOpen = false;
      state.publishBuildState = "pending";
      state.publishStatus = null;
      state.publishError = null;
    });
    builder.addCase(triggerTenantBuild.fulfilled, (state, action) => {
      state.loading = true;
      state.publishStatus = action.payload?.response || state.publishStatus;
    });
    builder.addCase(triggerTenantBuild.rejected, (state, action) => {
      state.loading = false;
      state.publishModalOpen = false;
      state.publishBuildState = "failed";
      state.publishError =
        action.payload?.errorMessage || "Unable to start publishing.";
    });

    builder.addCase(getTenantPublishStatus.pending, (state) => {
      state.loading = false;
    });
    builder.addCase(getTenantPublishStatus.fulfilled, (state, action) => {
      const publishStatus = action.payload?.response || action.payload || null;
      const normalizedStatus = String(publishStatus?.status || "").toUpperCase();

      state.publishStatus = publishStatus;
      state.loading = true;

      const shouldUpdatePublishModal =
        state.publishModalOpen || state.publishBuildState === "pending";

      if (
        shouldUpdatePublishModal &&
        ["SUCCESS", "SUCCEEDED", "COMPLETED", "COMPLETE", "PUBLISHED", "DONE"].includes(
          normalizedStatus
        )
      ) {
        state.publishModalOpen = false;
        state.publishBuildState = "success";
        state.publishError = null;
      } else if (
        shouldUpdatePublishModal &&
        ["FAILED", "FAILURE", "ERROR", "CANCELLED", "CANCELED"].includes(
          normalizedStatus
        )
      ) {
        state.publishModalOpen = false;
        state.publishBuildState = "failed";
        state.publishError = "Publishing failed. Please try again.";
      }
    });
    builder.addCase(getTenantPublishStatus.rejected, (state, action) => {
      state.loading = false;
      if (state.publishBuildState === "pending") {
        state.publishModalOpen = false;
        state.publishBuildState = "failed";
        state.publishError =
          action.payload?.errorMessage || "Unable to fetch publishing status.";
      }
    });
  },
});

export const { closeTenantPublishModal } = tenantSlice.actions;
export default tenantSlice.reducer;
